import React, { useState, useEffect } from "react";
import axios from "axios";
import { buildApiUrl } from "../config/api.config";
import AIBackgroundEffects from "./AIBackgroundEffects";
import "./PaymentManager.css";

const PaymentManager = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, paid, pending, failed
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    totalPaid: 0,
    totalPending: 0,
    totalFailed: 0,
  });

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(buildApiUrl("/api/admin/payments"), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        const paymentsData = response.data.data;
        setPayments(paymentsData);

        // Calculate statistics
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        const totalRevenue = paymentsData
          .filter((p) => p.status === "paid")
          .reduce((sum, p) => sum + p.amount, 0);

        const monthlyRevenue = paymentsData
          .filter(
            (p) => p.status === "paid" && new Date(p.paidAt) >= startOfMonth
          )
          .reduce((sum, p) => sum + p.amount, 0);

        const totalPaid = paymentsData.filter(
          (p) => p.status === "paid"
        ).length;
        const totalPending = paymentsData.filter(
          (p) => p.status === "pending"
        ).length;
        const totalFailed = paymentsData.filter(
          (p) => p.status === "failed"
        ).length;

        setStats({
          totalRevenue,
          monthlyRevenue,
          totalPaid,
          totalPending,
          totalFailed,
        });
      }
    } catch (error) {
      console.error("Error al cargar pagos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
  };

  const handleCloseModal = () => {
    setSelectedPayment(null);
  };

  const handleStatusChange = async (paymentId, newStatus) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(
        buildApiUrl(`/api/admin/payments/${paymentId}/status`),
        { status: newStatus },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Refresh payments
      fetchPayments();
      setSelectedPayment(null);
    } catch (error) {
      console.error("Error al actualizar estado:", error);
      alert("Error al actualizar el estado del pago");
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      paid: "✓ Pagado",
      pending: "⏳ Pendiente",
      failed: "❌ Fallido",
      refunded: "↩️ Reembolsado",
    };
    return labels[status] || status;
  };

  const filteredPayments = payments.filter((payment) => {
    if (filter !== "all" && payment.status !== filter) return false;
    if (
      searchTerm &&
      !payment.businessName.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !payment.email.toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="payment-loading">
        <div className="payment-manager-loading-spinner"></div>
        <p>Cargando pagos...</p>
      </div>
    );
  }

  return (
    <div className="payment-manager">
      <AIBackgroundEffects step={1} withSidebar={true} />
      {/* Header */}
      <div className="payment-header">
        <div>
          <h1>💰 Gestor de Pagos</h1>
          <p className="header-subtitle">
            Administración de transacciones y facturación
          </p>
        </div>
        <button
          className="add-payment-btn"
          onClick={() => setShowAddPayment(true)}
        >
          ➕ Registrar Pago
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="payment-stats">
        <div className="stat-card-payment revenue-card">
          <div className="payment-manager-stat-icon">💵</div>
          <div className="payment-manager-stat-content">
            <h3>Ingresos Totales</h3>
            <p className="payment-manager-stat-value">${stats.totalRevenue.toFixed(2)}</p>
            <span className="payment-manager-stat-label">
              {stats.totalPaid} pagos completados
            </span>
          </div>
        </div>
        <div className="stat-card-payment monthly-card">
          <div className="payment-manager-stat-icon">📊</div>
          <div className="payment-manager-stat-content">
            <h3>Ingresos del Mes</h3>
            <p className="payment-manager-stat-value">${stats.monthlyRevenue.toFixed(2)}</p>
            <span className="payment-manager-stat-label">Este mes</span>
          </div>
        </div>
        <div className="stat-card-payment pending-card">
          <div className="payment-manager-stat-icon">⏳</div>
          <div className="payment-manager-stat-content">
            <h3>Pagos Pendientes</h3>
            <p className="payment-manager-stat-value">{stats.totalPending}</p>
            <span className="payment-manager-stat-label">Por confirmar</span>
          </div>
        </div>
        <div className="stat-card-payment failed-card">
          <div className="payment-manager-stat-icon">❌</div>
          <div className="payment-manager-stat-content">
            <h3>Pagos Fallidos</h3>
            <p className="payment-manager-stat-value">{stats.totalFailed}</p>
            <span className="payment-manager-stat-label">Requieren atención</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="payment-filters">
        <div className="payment-manager-search-box">
          <span className="payment-manager-search-icon">🔍</span>
          <input
            type="text"
            placeholder="Buscar por negocio o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="payment-manager-filter-tabs">
          <button
            className={filter === "all" ? "active" : ""}
            onClick={() => setFilter("all")}
          >
            📊 Todos
          </button>
          <button
            className={filter === "paid" ? "active" : ""}
            onClick={() => setFilter("paid")}
          >
            ✓ Pagados
          </button>
          <button
            className={filter === "pending" ? "active" : ""}
            onClick={() => setFilter("pending")}
          >
            ⏳ Pendientes
          </button>
          <button
            className={filter === "failed" ? "active" : ""}
            onClick={() => setFilter("failed")}
          >
            ❌ Fallidos
          </button>
        </div>
      </div>

      {/* Payments Table */}
      <div className="payments-table-container">
        <table className="payments-table">
          <thead>
            <tr>
              <th>Negocio</th>
              <th>Email</th>
              <th>Monto</th>
              <th>Estado</th>
              <th>Fecha</th>
              <th>Método</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.length > 0 ? (
              filteredPayments.map((payment) => (
                <tr key={payment._id}>
                  <td>
                    <div className="business-cell">
                      <span className="business-icon">🏢</span>
                      {payment.businessName}
                    </div>
                  </td>
                  <td className="email-cell">{payment.email}</td>
                  <td className="amount-cell">${payment.amount.toFixed(2)}</td>
                  <td>
                    <span className={`payment-status ${payment.status}`}>
                      {getStatusLabel(payment.status)}
                    </span>
                  </td>
                  <td className="date-cell">
                    {new Date(payment.createdAt).toLocaleDateString("es-ES", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>
                  <td className="method-cell">
                    {payment.paymentMethod || "Manual"}
                  </td>
                  <td>
                    <button
                      className="payment-manager-view-btn"
                      onClick={() => handleViewDetails(payment)}
                    >
                      👁️ Ver
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="payment-manager-empty-row">
                  <div className="payment-manager-empty-state">
                    <p>💳 No hay pagos registrados</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Payment Details Modal */}
      {selectedPayment && (
        <div className="payment-manager-modal-overlay" onClick={handleCloseModal}>
          <div className="payment-manager-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="payment-manager-modal-header">
              <h2>Detalles del Pago</h2>
              <button className="payment-manager-close-btn" onClick={handleCloseModal}>
                ✕
              </button>
            </div>
            <div className="payment-manager-modal-body">
              <div className="payment-manager-detail-row">
                <span className="payment-manager-detail-label">Negocio:</span>
                <span className="payment-manager-detail-value">
                  {selectedPayment.businessName}
                </span>
              </div>
              <div className="payment-manager-detail-row">
                <span className="payment-manager-detail-label">Email:</span>
                <span className="payment-manager-detail-value">{selectedPayment.email}</span>
              </div>
              <div className="payment-manager-detail-row">
                <span className="payment-manager-detail-label">Monto:</span>
                <span className="payment-manager-detail-value amount">
                  ${selectedPayment.amount.toFixed(2)}
                </span>
              </div>
              <div className="payment-manager-detail-row">
                <span className="payment-manager-detail-label">Estado:</span>
                <span className={`payment-status ${selectedPayment.status}`}>
                  {getStatusLabel(selectedPayment.status)}
                </span>
              </div>
              <div className="payment-manager-detail-row">
                <span className="payment-manager-detail-label">Método de Pago:</span>
                <span className="payment-manager-detail-value">
                  {selectedPayment.paymentMethod || "Manual"}
                </span>
              </div>
              <div className="payment-manager-detail-row">
                <span className="payment-manager-detail-label">Fecha de Pago:</span>
                <span className="payment-manager-detail-value">
                  {new Date(selectedPayment.createdAt).toLocaleString("es-ES")}
                </span>
              </div>
              {selectedPayment.transactionId && (
                <div className="payment-manager-detail-row">
                  <span className="payment-manager-detail-label">ID Transacción:</span>
                  <span className="payment-manager-detail-value transaction-id">
                    {selectedPayment.transactionId}
                  </span>
                </div>
              )}
              {selectedPayment.notes && (
                <div className="payment-manager-detail-row">
                  <span className="payment-manager-detail-label">Notas:</span>
                  <span className="payment-manager-detail-value">{selectedPayment.notes}</span>
                </div>
              )}
            </div>

            {/* Status Change Actions */}
            <div className="modal-actions">
              <h4
                style={{
                  margin: "0 0 12px 0",
                  color: "#94a3b8",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                Cambiar Estado del Pago:
              </h4>
              <div className="status-buttons">
                {selectedPayment.status !== "paid" && (
                  <button
                    onClick={() =>
                      handleStatusChange(selectedPayment._id, "paid")
                    }
                    className="status-btn status-btn-paid"
                  >
                    ✓ Marcar como Pagado
                  </button>
                )}
                {selectedPayment.status !== "pending" && (
                  <button
                    onClick={() =>
                      handleStatusChange(selectedPayment._id, "pending")
                    }
                    className="status-btn status-btn-pending"
                  >
                    ⏳ Marcar como Pendiente
                  </button>
                )}
                {selectedPayment.status !== "failed" && (
                  <button
                    onClick={() =>
                      handleStatusChange(selectedPayment._id, "failed")
                    }
                    className="status-btn status-btn-failed"
                  >
                    ❌ Marcar como Fallido
                  </button>
                )}
                {selectedPayment.status === "paid" &&
                  selectedPayment.status !== "refunded" && (
                    <button
                      onClick={() =>
                        handleStatusChange(selectedPayment._id, "refunded")
                      }
                      className="status-btn status-btn-refunded"
                    >
                      ↩️ Reembolsar
                    </button>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Effects */}
      <div className="payment-effects">
        <div className="money-particle particle-1">$</div>
        <div className="money-particle particle-2">$</div>
        <div className="money-particle particle-3">$</div>
        <div className="glow-orb orb-1"></div>
        <div className="glow-orb orb-2"></div>
      </div>
    </div>
  );
};

export default PaymentManager;
